/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Children } from 'react';
import { warn } from '@instructure/console';
import { generateId } from '@instructure/ui-utils';
import { matchComponentTypes } from '@instructure/ui-react-utils';
import { Drilldown } from '@instructure/ui-drilldown';
import { TopNavBarItem } from "../TopNavBarItem/index.js";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createElement as _createElement } from "@emotion/react";
const mapItemsForDrilldown = (itemList, options = {}) => {
  const submenus = [];
  const currentPageId = options.currentPageId,
    renderOptionContent = options.renderOptionContent;
  const customPopoverIdMap = new Map();
  Children.forEach(itemList, item => {
    if (!item || !matchComponentTypes(item, [TopNavBarItem])) return;
    const _item$props = item.props,
      renderSubmenu = _item$props.renderSubmenu,
      customPopoverConfig = _item$props.customPopoverConfig,
      id = _item$props.id,
      children = _item$props.children,
      status = _item$props.status,
      _item$props$variant = _item$props.variant,
      variant = _item$props$variant === void 0 ? 'default' : _item$props$variant,
      href = _item$props.href,
      onClick = _item$props.onClick,
      shouldCloseOnClick = _item$props.shouldCloseOnClick;
    let submenu = renderSubmenu;
    let customPopover = customPopoverConfig;
    let customPopoverId;
    let optionSubPageId;
    const submenuPages = [];
    if (submenu) {
      if (!matchComponentTypes(submenu, [Drilldown])) {
        warn(false, `The "renderSubmenu" prop accepts only Drilldown components, but the item with id "${id}" received: "${renderSubmenu}".`);
        submenu = void 0;
      }

      // if still has submenu...
      if (submenu) {
        optionSubPageId = submenu.props.rootPageId;
        submenuPages.push(...Children.toArray(submenu.props.children));
      }
    }
    if (customPopover) {
      if (!customPopover.children) {
        warn(false, `Pass the content of the custom Popover as "customPopoverConfig.children" for the item with id: "${id}".`);
        customPopover = void 0;
      }
      if (submenu) {
        warn(false, `TopNavBar.Items are not allowed to have both the "renderSubmenu" and "customPopoverConfig" props. For submenus, pass a Drilldown component via the "renderSubmenu" prop, and only use "customPopoverConfig" for custom features. Item with id: "${id}" will ignore the "customPopoverConfig" prop.`);
        customPopover = void 0;
      }

      // if still has customPopover...
      if (customPopover) {
        customPopoverId = generateId(`TopNavBarItem__customPopoverOption`, customPopoverIdMap);
        optionSubPageId = customPopoverId;
        submenuPages.push(_jsx(Drilldown.Page, {
          id: customPopoverId,
          children: _jsx(Drilldown.Option, {
            id: `${customPopoverId}__option`,
            children: customPopover.children
          })
        }, customPopoverId));
      }
    }
    let ariaCurrent = item.props['aria-current'] || (id === currentPageId ? 'page' : void 0);
    if (ariaCurrent) {
      if (variant !== 'default') {
        warn(false, `Only \`variant="default"\` items can be set to current/active, but the item with id "${id}" is "${variant}" variant.`);
        ariaCurrent = void 0;
      }
      if (status === 'disabled') {
        warn(false, `Disabled items can not be set to current/active, but the item with id "${id}" is disabled.`);
        ariaCurrent = void 0;
      }
    }
    submenus.push({
      item,
      submenuPages,
      optionData: {
        id,
        disabled: status === 'disabled',
        href,
        onOptionClick: event => {
          onClick === null || onClick === void 0 ? void 0 : onClick(event);
        },
        children: typeof renderOptionContent === 'function' ? renderOptionContent(children, {
          ...item.props,
          status: ariaCurrent === 'page' ? 'active' : item.props.status
        }) : children,
        subPageId: optionSubPageId,
        'aria-current': ariaCurrent,
        shouldCloseOnClick: shouldCloseOnClick
      }
    });
  });
  return submenus;
};
const renderMappedItemDrilldownSubpages = mappedItems => {
  const subPages = mappedItems.map(option => option.submenuPages).filter(submenu => !!submenu);
  return subPages.flat();
};
const renderMappedItemsAsDrilldownOptions = mappedItems => {
  return mappedItems.map(mappedItem => {
    const optionData = mappedItem.optionData,
      originalItem = mappedItem.item;
    const onHiddenClick = originalItem.props.onHiddenClick;
    if (onHiddenClick) {
      // if the `onHiddenClick` prop is provided for the MenuItem it should overwrite the `onClick` prop if there is any
      optionData.onOptionClick = onHiddenClick;
    }
    return _createElement(Drilldown.Option, {
      ...optionData,
      key: optionData.id,
      afterLabelContentVAlign: "center"
    }, optionData.children);
  });
};
export default mapItemsForDrilldown;
export { mapItemsForDrilldown, renderMappedItemDrilldownSubpages, renderMappedItemsAsDrilldownOptions };